import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import {
  getSetupContextFor,
  setSetupContext,
  clearSetupContext,
} from "../../utilities/setupContext";
import {
  buildRequirements,
  computeReadiness,
  firstIncompleteIndex,
} from "../../utilities/checkInReadiness";
import { getSource } from "./sources";
import { getStepComponent } from "./steps";
import SetupProgress from "./components/SetupProgress";
import SetupComplete from "./components/SetupComplete";

const CenteredSpinner = ({ label }) => (
  <Grid
    container
    direction="column"
    alignItems="center"
    justifyContent="center"
    spacing={2}
    sx={{ minHeight: "60vh" }}
  >
    <Grid item>
      <CircularProgress />
    </Grid>
    {label && (
      <Grid item>
        <Typography variant="subtitle1">{label}</Typography>
      </Grid>
    )}
  </Grid>
);

const StepContainer = ({ bleed, children }) =>
  bleed ? (
    children
  ) : (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {children}
    </Container>
  );

const Setup = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [descriptor, setDescriptor] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const [questions, setQuestions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [index, setIndex] = React.useState(0);
  const [checkedIn, setCheckedIn] = React.useState(false);
  const [blocked, setBlocked] = React.useState(null);

  const checkInAttempted = React.useRef(false);

  const loadProfileAndQuestions = React.useCallback(async (ctx) => {
    const profileResult = await request("profile");
    if (!profileResult?.success) {
      throw new Error(profileResult?.error || "Unable to load your profile.");
    }
    const nextProfile = profileResult.profile;

    let nextQuestions = [];
    if (ctx.questionGroupId) {
      const questionResult = await request(
        `profilequestions/bygroup/?questionGroupId=${ctx.questionGroupId}`
      );
      if (!questionResult?.success) {
        throw new Error(questionResult?.error || "Unable to load your questions.");
      }
      nextQuestions = questionResult.profileQuestions || [];
    }
    return { nextProfile, nextQuestions };
  }, []);

  React.useEffect(() => {
    const prime = async () => {
      const fromState = location.state?.setup;
      const fresh =
        fromState && String(fromState.eventId) === String(eventId)
          ? fromState
          : null;
      const stored = fresh || getSetupContextFor(eventId);
      const source = getSource(stored?.source);

      if (!stored || !source) {
        navigate("/");
        return;
      }

      let ctx = stored;
      try {
        if (!fresh && source.refresh) {
          const refreshed = await source.refresh(stored);
          if (refreshed) ctx = refreshed;
        }
      } catch (e) {
        /* keep the stored descriptor */
      }

      setSetupContext(ctx);
      setDescriptor(ctx);

      try {
        const { nextProfile, nextQuestions } = await loadProfileAndQuestions(ctx);
        setProfile(nextProfile);
        setQuestions(nextQuestions);

        const requirements = buildRequirements({
          profile: nextProfile,
          descriptor: ctx,
          questions: nextQuestions,
        });
        setIndex(firstIncompleteIndex(requirements));
      } catch (e) {
        setBlocked(t("setup.error.load"));
      } finally {
        setLoading(false);
      }
    };
    prime();
  }, [eventId, location.state, navigate, loadProfileAndQuestions, t]);

  const requirements = React.useMemo(
    () => buildRequirements({ profile, descriptor, questions }),
    [profile, descriptor, questions]
  );

  const readiness = React.useMemo(
    () => computeReadiness(requirements),
    [requirements]
  );

  // Satisfying the last requirement is not the same as being done with the
  // step: a multi-select or a text answer becomes "meaningful" on the first
  // selection or keystroke, while the attendee is still working. Wait until
  // they have actually moved past the final step before checking anyone in.
  const finished = readiness.ready && index >= requirements.length;

  const finishSetup = React.useCallback(() => {
    const source = getSource(descriptor?.source);
    if (source?.onComplete) source.onComplete(descriptor);
    clearSetupContext();
  }, [descriptor]);

  React.useEffect(() => {
    if (loading || checkedIn || !descriptor) return;
    if (!finished) return;
    if (descriptor.completion !== "checkin") return;
    if (descriptor.checkInDisabled) {
      setBlocked(t("setup.checkin.disabled"));
      return;
    }
    if (checkInAttempted.current) return;
    checkInAttempted.current = true;

    const checkIn = async () => {
      const result = await request("events/checkin", "post", {
        eventId: descriptor.eventId,
      });
      if (result?.success) {
        finishSetup();
        setCheckedIn(true);
        return;
      }

      const reason =
        result?.message || result?.error || t("setup.checkin.failed");
      checkInAttempted.current = false;
      toast.error(reason);

      try {
        const { nextProfile, nextQuestions } = await loadProfileAndQuestions(
          descriptor
        );
        setProfile(nextProfile);
        setQuestions(nextQuestions);
        const refreshed = buildRequirements({
          profile: nextProfile,
          descriptor,
          questions: nextQuestions,
        });
        const next = firstIncompleteIndex(refreshed);
        if (next >= refreshed.length) {
          setBlocked(reason);
          return;
        }
        setIndex(next);
      } catch (e) {
        setBlocked(reason);
      }
    };
    checkIn();
  }, [
    loading,
    checkedIn,
    descriptor,
    finished,
    finishSetup,
    loadProfileAndQuestions,
    t,
  ]);

  const handleAnswer = (questionId, answer) => {
    setQuestions((current) =>
      current.map((question) =>
        question._id === questionId ? { ...question, answer } : question
      )
    );
    if (answer === null || answer === undefined) return;

    request("profilequestions/answer", "post", { questionId, answer }).then(
      (result) => {
        if (result?.success) return;
        toast.error(t("setup.question.error"));
        setQuestions((current) =>
          current.map((question) =>
            question._id === questionId
              ? { ...question, answer: null }
              : question
          )
        );
      }
    );
  };

  const advance = () => {
    setIndex((current) => current + 1);
  };

  const refreshProfile = async () => {
    const result = await request("profile");
    if (!result?.success) return;
    setProfile((current) => ({ ...current, ...result.profile }));
  };

  if (loading) return <CenteredSpinner label={t("setup.loading")} />;

  if (checkedIn) return <SetupComplete descriptor={descriptor} />;

  if (blocked) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {descriptor?.eventName}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {blocked}
        </Typography>
        <Button
          className="tnfm-pill-btn"
          variant="contained"
          fullWidth
          sx={{ mb: 1.5 }}
          onClick={() => navigate("/profile")}
        >
          {t("setup.blocked.profile")}
        </Button>
        <Button
          className="tnfm-pill-btn"
          variant="text"
          fullWidth
          onClick={() => navigate("/")}
        >
          {t("setup.blocked.myevents")}
        </Button>
      </Container>
    );
  }

  if (finished) {
    return descriptor?.completion === "checkin" ? (
      <CenteredSpinner label={t("setup.checkingin")} />
    ) : (
      <SetupComplete descriptor={descriptor} />
    );
  }

  const effectiveIndex =
    index >= requirements.length ? firstIncompleteIndex(requirements) : index;
  const current = requirements[Math.min(effectiveIndex, requirements.length - 1)];
  const StepComponent = getStepComponent(current?.type);
  if (!StepComponent) return <CenteredSpinner />;

  const question =
    current.type === "question"
      ? questions.find((q) => q._id === current.questionId)
      : null;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <SetupProgress
        eventName={descriptor?.eventName}
        stepNumber={Math.min(effectiveIndex, requirements.length - 1) + 1}
        total={readiness.total}
        onBack={
          effectiveIndex > 0
            ? () => setIndex(Math.max(0, effectiveIndex - 1))
            : null
        }
      />
      <StepContainer bleed={StepComponent.bleed}>
        <StepComponent
          key={current.key}
          profile={profile}
          question={question}
          descriptor={descriptor}
          onAnswer={handleAnswer}
          onComplete={(updates) => {
            if (current.type === "name" && updates) {
              setProfile((currentProfile) => ({
                ...currentProfile,
                ...updates,
              }));
            }
            if (current.type === "photo") {
              setProfile((currentProfile) => ({
                ...currentProfile,
                profileImage: true,
              }));
              refreshProfile();
            }
            advance();
          }}
        />
      </StepContainer>
    </Box>
  );
};

export default Setup;
